import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, CircleMarker, Popup, useMap } from "react-leaflet";
import { Link } from "react-router-dom";
import { ChartLine, Funnel, Fire, FlowArrow, MapTrifold, Lightning } from "@phosphor-icons/react";
import MapBaseLayers from "../components/map/MapBaseLayers";
import MapSearchControl from "../components/map/MapSearchControl";
import HeatmapLayer from "../components/map/HeatmapLayer";
import { useCounty } from "../lib/CountyContext";
import { fetchHistoricFires, fetchHistoricStats, api } from "../lib/api";

const DEFAULT_CENTER = [47.55, -120.0];
const DEFAULT_ZOOM = 7;

function fmtAcres(a) {
  if (a == null) return "—";
  if (a >= 1000) return `${(a / 1000).toFixed(1)}k`;
  if (a >= 100) return Math.round(a).toLocaleString();
  return a.toFixed(1);
}

function ResizeFix() {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 250);
    return () => clearTimeout(t);
  }, [map]);
  return null;
}

function FlyTo({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) map.flyTo(position, 10, { duration: 0.8 });
  }, [position, map]);
  return null;
}

export default function HistoryPage() {
  const { county, current } = useCounty();
  const [years, setYears] = useState(3);
  const [yearFilter, setYearFilter] = useState(null); // null = all years
  const [mode, setMode] = useState("acres"); // "acres" | "density"
  const [minAcres, setMinAcres] = useState(0);
  const [fires, setFires] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [focus, setFocus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      fetchHistoricFires({ county: county !== "all" ? county : undefined, years, min_acres: minAcres }),
      fetchHistoricStats({ county: county !== "all" ? county : undefined, years }),
    ])
      .then(([f, s]) => {
        if (cancelled) return;
        setFires(f || []);
        setStats(s || null);
      })
      .catch((err) => console.warn("history load failed", err))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [county, years, minAcres]);

  const shownFires = useMemo(
    () => (yearFilter ? fires.filter((f) => f.year === yearFilter) : fires),
    [fires, yearFilter]
  );

  const heatPoints = useMemo(() => {
    if (!shownFires.length) return [];
    const maxAcres = Math.max(1, ...shownFires.map((f) => f.acres || 0));
    return shownFires.map((f) => {
      const weight =
        mode === "acres"
          ? Math.min(1.0, Math.sqrt((f.acres || 0) / maxAcres))
          : 0.5; // uniform for density
      return { lat: f.latitude, lon: f.longitude, weight };
    });
  }, [shownFires, mode]);

  const topFires = useMemo(
    () => [...shownFires].sort((a, b) => (b.acres || 0) - (a.acres || 0)).slice(0, 25),
    [shownFires]
  );

  const countyName = current?.name || "Washington";

  return (
    <div className="flex flex-col lg:flex-row h-[calc(100vh-3.5rem)] bg-tac-bg" data-testid="history-page">
      {/* Sidebar */}
      <aside className="lg:w-[400px] xl:w-[440px] border-r border-tac-border bg-tac-bg/80 overflow-y-auto tac-scroll">
        <header className="px-4 py-4 border-b border-tac-border bg-tac-surface/40">
          <div className="flex items-center gap-2 text-tac-primary mb-1">
            <ChartLine size={18} weight="duotone" />
            <h1 className="font-display font-black text-[15px] uppercase tracking-wider text-tac-text">
              Fire History
            </h1>
          </div>
          <p className="text-[14px] font-mono-tac text-tac-faint uppercase tracking-wider">
            {countyName} · Last {years} years · NIFC IFPH
          </p>
        </header>

        {/* Filters */}
        <section className="px-4 py-3 border-b border-tac-border space-y-3" data-testid="history-filters">
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <Funnel size={11} weight="bold" className="text-tac-dim" />
              <span className="text-[15px] font-mono-tac uppercase tracking-wider text-tac-dim">
                Time window
              </span>
            </div>
            <div className="flex gap-1.5">
              {[1, 3, 5, 10].map((y) => (
                <button
                  key={y}
                  onClick={() => { setYears(y); setYearFilter(null); }}
                  data-testid={`history-years-${y}`}
                  className={`flex-1 px-2 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm border transition-colors ${
                    years === y
                      ? "border-tac-primary bg-tac-primary/15 text-tac-primary"
                      : "border-tac-border bg-tac-surface text-tac-dim hover:text-tac-text hover:border-tac-borderBright"
                  }`}
                >
                  {y}y
                </button>
              ))}
            </div>
          </div>

          <div data-testid="history-year-scrub">
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <div className="flex items-center gap-1.5">
                <ChartLine size={11} weight="bold" className="text-tac-dim" />
                <span className="text-[15px] font-mono-tac uppercase tracking-wider text-tac-dim">
                  Scrub by year
                </span>
              </div>
              <span data-testid="history-year-label" className="text-[12px] font-bold font-mono-tac text-tac-primary uppercase">
                {yearFilter ?? "All years"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="range"
                data-testid="history-year-slider"
                min={new Date().getFullYear() - years}
                max={new Date().getFullYear()}
                step={1}
                value={yearFilter ?? new Date().getFullYear()}
                onChange={(e) => setYearFilter(Number(e.target.value))}
                className="flex-1 accent-[#F2542D] cursor-pointer"
              />
              <button
                onClick={() => setYearFilter(null)}
                data-testid="history-year-all"
                className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border transition-colors ${
                  yearFilter === null
                    ? "border-tac-primary bg-tac-primary/15 text-tac-primary"
                    : "border-tac-border bg-tac-surface text-tac-dim hover:text-tac-text"
                }`}
              >
                All
              </button>
            </div>
            {yearFilter && (
              <div className="mt-1 text-[11px] font-mono-tac text-tac-faint">
                {shownFires.length} fires in {yearFilter}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <Fire size={11} weight="bold" className="text-tac-dim" />
              <span className="text-[15px] font-mono-tac uppercase tracking-wider text-tac-dim">
                Heatmap mode
              </span>
            </div>
            <div className="flex gap-1.5">
              <button
                onClick={() => setMode("acres")}
                data-testid="history-mode-acres"
                className={`flex-1 px-2 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm border transition-colors ${
                  mode === "acres"
                    ? "border-tac-primary bg-tac-primary/15 text-tac-primary"
                    : "border-tac-border bg-tac-surface text-tac-dim hover:text-tac-text"
                }`}
              >
                By acres
              </button>
              <button
                onClick={() => setMode("density")}
                data-testid="history-mode-density"
                className={`flex-1 px-2 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm border transition-colors ${
                  mode === "density"
                    ? "border-tac-primary bg-tac-primary/15 text-tac-primary"
                    : "border-tac-border bg-tac-surface text-tac-dim hover:text-tac-text"
                }`}
              >
                By count
              </button>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[15px] font-mono-tac uppercase tracking-wider text-tac-dim">
                Min size
              </span>
              <span className="text-[11px] font-mono-tac tabular text-tac-text">
                {minAcres === 0 ? "any" : `≥ ${minAcres.toLocaleString()} ac`}
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={5000}
              step={100}
              value={minAcres}
              onChange={(e) => setMinAcres(Number(e.target.value))}
              data-testid="history-min-acres"
              className="w-full accent-tac-primary"
            />
          </div>
        </section>

        {/* Stats */}
        {stats && (
          <section className="px-4 py-3 border-b border-tac-border" data-testid="history-stats">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[15px] font-mono-tac uppercase tracking-wider text-tac-faint">
                  Total fires
                </div>
                <div className="font-display font-black text-2xl text-tac-text tabular">
                  {stats.total_fires}
                </div>
              </div>
              <div>
                <div className="text-[15px] font-mono-tac uppercase tracking-wider text-tac-faint">
                  Acres burned
                </div>
                <div className="font-display font-black text-2xl text-tac-primary tabular">
                  {fmtAcres(stats.total_acres)}
                </div>
              </div>
            </div>
            {stats.by_year?.length > 0 && (
              <div className="mt-3 space-y-1">
                {stats.by_year.map((y) => (
                  <YearBar key={y.year} year={y} maxAcres={Math.max(...stats.by_year.map((x) => x.total_acres))} />
                ))}
              </div>
            )}
          </section>
        )}

        {/* Storm days + lightning-caused fires */}
        <StormDaySection county={county} />
        <LightningFiresSection county={county} onFocus={setFocus} />

        {/* Top fires list */}
        <section className="px-4 py-3" data-testid="history-list">
          <div className="text-[15px] font-mono-tac uppercase tracking-wider text-tac-dim mb-2">
            Largest fires {minAcres > 0 && `(≥ ${minAcres} ac)`}
          </div>
          {loading && (
            <div className="text-[14px] font-mono-tac text-tac-faint italic py-4 text-center">
              Loading historic perimeters…
            </div>
          )}
          {!loading && topFires.length === 0 && (
            <div className="text-[14px] font-mono-tac text-tac-faint italic py-4 text-center">
              No historic fires match these filters.
            </div>
          )}
          <ul className="space-y-1">
            {topFires.map((f) => (
              <li key={f.id}>
                <button
                  data-testid={`history-fire-${f.id}`}
                  onClick={() => setFocus([f.latitude, f.longitude])}
                  className="w-full text-left px-2 py-1.5 rounded-sm hover:bg-tac-surface border border-transparent hover:border-tac-borderBright transition-colors group"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[12px] font-bold text-tac-text uppercase tracking-tight truncate">
                      {f.name}
                    </span>
                    <span className="text-[15px] font-mono-tac text-tac-faint shrink-0">
                      {f.year}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <span className="text-[15px] font-mono-tac uppercase tracking-wider text-tac-dim">
                      {f.county_slug || "—"}
                    </span>
                    <span className="text-[10px] font-mono-tac tabular text-tac-primary">
                      {fmtAcres(f.acres)} ac
                    </span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </section>

        <footer className="px-4 py-3 border-t border-tac-border">
          <Link to="/" className="inline-flex items-center gap-1.5 text-[14px] font-bold uppercase tracking-wider text-tac-dim hover:text-tac-primary transition-colors" data-testid="back-to-map">
            <MapTrifold size={12} weight="bold" />
            Back to live map
          </Link>
          <p className="mt-2 text-[10px] font-mono-tac text-tac-faint leading-relaxed">
            Source: NIFC InterAgencyFirePerimeterHistory All Years View · 2024 perimeters
            current; new years released annually mid-year by NIFC.
          </p>
        </footer>
      </aside>

      {/* Map */}
      <main className="flex-1 relative">
        <MapContainer
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          minZoom={5}
          maxZoom={14}
          className="absolute inset-0 z-0"
          worldCopyJump={false}
          attributionControl={false}
        >
          <MapBaseLayers />
          <MapSearchControl />
          <ResizeFix />
          <FlyTo position={focus} />
          <HeatmapLayer points={heatPoints} />
          {topFires.map((f) => (
            <CircleMarker
              key={`pin-${f.id}`}
              center={[f.latitude, f.longitude]}
              radius={Math.max(3, Math.min(10, Math.log10((f.acres || 1) + 10) * 2))}
              pathOptions={{
                color: "#0A0D0C",
                weight: 0.8,
                fillColor: "#FFD56B",
                fillOpacity: 0.85,
              }}
            >
              <Popup>
                <div className="min-w-[180px]" data-testid={`history-popup-${f.id}`}>
                  <div className="font-bold text-[12px] uppercase tracking-tight">{f.name}</div>
                  <div className="text-[14px] font-mono-tac text-tac-dim mt-1 tabular space-y-0.5">
                    <div>Year: {f.year}</div>
                    <div>Acres: {fmtAcres(f.acres)}</div>
                    {f.county_slug && <div>County: {f.county_slug}</div>}
                    {f.agency && <div>Agency: {f.agency}</div>}
                  </div>
                </div>
              </Popup>
            </CircleMarker>
          ))}
        </MapContainer>

        {/* Legend overlay */}
        <div className="absolute bottom-3 left-3 z-[400] bg-tac-bg/85 backdrop-blur-sm border border-tac-border rounded-sm px-3 py-2 text-[15px] font-mono-tac uppercase tracking-wider text-tac-dim" data-testid="history-legend">
          <div className="flex items-center gap-1.5 mb-1 text-tac-text">
            <FlowArrow size={12} weight="bold" />
            <span>{mode === "acres" ? "Heatmap by acres burned" : "Heatmap by fire count"}</span>
          </div>
          <div className="flex items-center gap-1">
            <span>cool</span>
            <div className="h-1.5 w-32 rounded-sm" style={{
              background: "linear-gradient(90deg, #1E293B 0%, #5B21B6 20%, #DC2626 40%, #EA580C 60%, #F59E0B 80%, #FEF3C7 100%)"
            }} />
            <span>hot</span>
          </div>
          <div className="mt-1.5 text-[10px] text-tac-faint">
            Top {topFires.length} fires shown as gold pins
          </div>
        </div>
      </main>
    </div>
  );
}

function StormDaySection({ county }) {
  const [days, setDays] = useState(null);

  useEffect(() => {
    api.get("/lightning/daily", { params: { days: 30, county: county !== "all" ? county : undefined } })
      .then(({ data }) => setDays(data.days || []))
      .catch(() => setDays([]));
  }, [county]);

  if (days === null || days.length === 0) return null;
  const max = Math.max(1, ...days.map((d) => d.total));
  const stormDays = days.filter((d) => d.total > 0).length;
  const total = days.reduce((s, d) => s + d.total, 0);

  const fmtDay = (iso) => {
    const [, m, dd] = iso.split("-");
    return `${Number(m)}/${Number(dd)}`;
  };

  return (
    <section className="px-4 py-3 border-b border-tac-border" data-testid="storm-day-chart">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5">
          <Lightning size={12} weight="fill" className="text-[#FFD60A]" />
          <span className="text-[15px] font-mono-tac uppercase tracking-wider text-tac-dim">
            Storm days · last 30d
          </span>
        </div>
        <span className="text-[11px] font-mono-tac tabular text-tac-text">
          <b className="text-[#FFD60A]">{total.toLocaleString()}</b> strikes · {stormDays} storm day{stormDays !== 1 ? "s" : ""}
        </span>
      </div>
      <div className="flex items-end gap-[2px] h-16 mt-2" data-testid="storm-day-bars">
        {days.map((d) => (
          <div
            key={d.date}
            title={`${d.date}: ${d.total.toLocaleString()} strike${d.total !== 1 ? "s" : ""}`}
            className="flex-1 rounded-t-[1px] min-w-0"
            style={{
              height: d.total > 0 ? `${Math.max(8, (d.total / max) * 100)}%` : "2px",
              background: d.total > 0 ? "#FFD60A" : "#2A2F2C",
              opacity: d.total > 0 ? 0.55 + 0.45 * (d.total / max) : 1,
            }}
          />
        ))}
      </div>
      <div className="flex justify-between mt-1 text-[10px] font-mono-tac text-tac-faint">
        <span>{fmtDay(days[0].date)}</span>
        <span>{fmtDay(days[Math.floor(days.length / 2)].date)}</span>
        <span>{fmtDay(days[days.length - 1].date)}</span>
      </div>
    </section>
  );
}

function LightningFiresSection({ county, onFocus }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/lightning/flagged-fires")
      .then(({ data }) => setData(data))
      .catch(() => setData({ fires: [], count: 0 }));
  }, []);

  if (data === null) return null;
  const fires = county !== "all"
    ? (data.fires || []).filter((f) => (f.county || "").toLowerCase() === county)
    : (data.fires || []);

  return (
    <section className="px-4 py-3 border-b border-tac-border" data-testid="lightning-fires-section">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Lightning size={12} weight="fill" className="text-[#FFD60A]" />
          <span className="text-[15px] font-mono-tac uppercase tracking-wider text-tac-dim">
            Lightning starts · this season
          </span>
        </div>
        <span data-testid="lightning-fires-count" className="text-[12px] font-bold font-mono-tac tabular text-[#FFD60A]">
          {fires.length}
        </span>
      </div>
      {fires.length === 0 && (
        <div className="text-[13px] font-mono-tac text-tac-faint italic">
          No fires flagged with lightning near their start point yet.
        </div>
      )}
      <ul className="space-y-1">
        {fires.slice(0, 10).map((f) => (
          <li key={f.id}>
            <button
              data-testid={`lightning-fire-${f.id}`}
              onClick={() => f.latitude != null && onFocus([f.latitude, f.longitude])}
              className="w-full text-left px-2 py-1.5 rounded-sm hover:bg-tac-surface border border-transparent hover:border-tac-borderBright transition-colors"
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[12px] font-bold text-tac-text uppercase tracking-tight truncate">
                  ⚡ {f.name}
                </span>
                <span className="text-[10px] font-mono-tac tabular text-tac-primary shrink-0">
                  {f.acres != null ? `${fmtAcres(Number(f.acres))} ac` : "—"}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2 mt-0.5 text-[15px] font-mono-tac uppercase tracking-wider text-tac-dim">
                <span>{f.county || "—"}</span>
                <span className="text-tac-faint normal-case">
                  {f.strikes} strike{f.strikes !== 1 ? "s" : ""} · {f.nearest_mi} mi from start
                </span>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function YearBar({ year, maxAcres }) {
  const pct = maxAcres > 0 ? Math.min(100, (year.total_acres / maxAcres) * 100) : 0;
  return (
    <div className="flex items-center gap-2" data-testid={`history-year-${year.year}`}>
      <span className="text-[15px] font-mono-tac tabular text-tac-dim w-8">{year.year}</span>
      <div className="flex-1 h-2.5 bg-tac-surface rounded-sm overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-tac-primary to-amber-400"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-[10px] font-mono-tac tabular text-tac-text w-16 text-right">
        {fmtAcres(year.total_acres)} ac
      </span>
      <span className="text-[15px] font-mono-tac text-tac-faint w-8 text-right">×{year.count}</span>
    </div>
  );
}
